import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Outlet, Navigate } from 'react-router-dom';

// ── Eager: app shell + the landing route ──────────────────────────────────
// These ship in the main chunk. Home is the overwhelming majority of entries,
// so lazy-loading it would only add a network round trip before first paint.
import Footer from './components/Footer';
import Menu from './components/Menu';
import ScrollToTop from './components/ScrollToTop';
import { ProtectedRoute } from './components/ProtectedRoute';
import { AdminHeader } from './components/AdminHeader';
import { RouteFallback } from './components/RouteFallback';
import { Home } from './Pages/Home';

// ── Lazy: every other route ───────────────────────────────────────────────
// Each import() becomes its own webpack chunk, so a visitor reading the blog
// never downloads Leaflet (/find-food-bank) or the admin CMS. Most Pages use
// named exports, hence the .then() shim to the default shape React.lazy wants.
const named = (loader, key) => lazy(() => loader().then((m) => ({ default: m[key] })));

const BlogPage = named(() => import('./Pages/BlogPage'), 'BlogPage');
const CurrStory = named(() => import('./components/CurrentStory'), 'CurrStory');
const OliFamily = named(() => import('./Pages/OliFamily'), 'OliFamily');
const DonatePage = named(() => import('./Pages/DonatePage'), 'DonatePage');
const DonationSuccess = named(() => import('./Pages/DonationSuccess'), 'DonationSuccess');
const PatrioticGardens = named(() => import('./Pages/PatrioticGardens'), 'PatrioticGardens');
const PatrioticGardensEvent = named(() => import('./Pages/HomeDepot'), 'PatrioticGardensEvent');
const FallenHerosPage = named(() => import('./Pages/FallenHerosPage'), 'FallenHerosPage');
const History = named(() => import('./Pages/History'), 'History');
const GoldStarFam = named(() => import('./Pages/GoldStarFam'), 'GoldStarFam');
const AnnualGala = named(() => import('./Pages/AnnualGala'), 'AnnualGala');
const GalaHonorees = named(() => import('./Pages/GalaHonorees'), 'GalaHonorees');
const Programs = named(() => import('./Pages/Programs'), 'Programs');
const Faq = named(() => import('./Pages/Faq'), 'Faq');
const MeetGreetGrow = named(() => import('./Pages/MeetGreetGrow'), 'MeetGreetGrow');

// FindFoodBank is a default export and pulls in Leaflet + react-leaflet —
// by far the heaviest single route, and one most visitors never open.
const FindFoodBank = lazy(() => import('./Pages/FindFoodBank'));

const AdminLogin = named(() => import('./Pages/AdminLogin'), 'AdminLogin');
const AdminDashboard = named(() => import('./Pages/AdminDashboard'), 'AdminDashboard');
const AdminBlog = named(() => import('./Pages/AdminBlog'), 'AdminBlog');
const AdminArchive = named(() => import('./Pages/AdminArchive'), 'AdminArchive');
const AdminEditBlog = named(() => import('./Pages/AdminEditBlog'), 'AdminEditBlog');
const AdminEventRegistrations = named(() => import('./Pages/AdminEventRegistrations'), 'AdminEventRegistrations');

const MainLayout = () => (
  <div className="min-h-screen flex flex-col">
    <Menu />
    <main className="flex-grow">
      {/* Suspense sits inside the shell so the header/footer stay painted
          while a route chunk streams in — no full-page blank flash. */}
      <Suspense fallback={<RouteFallback />}>
        <Outlet />
      </Suspense>
    </main>
    <Footer />
  </div>
);

const AdminLayout = () => (
  <div className="min-h-screen flex flex-col">
    <AdminHeader />
    <main className="flex-grow">
      <Suspense fallback={<RouteFallback />}>
        <Outlet />
      </Suspense>
    </main>
  </div>
);

function App() {
  return (
    <Router>
      <ScrollToTop />
      <div>
        <Routes>
          <Route element={<MainLayout />}>
            {/* Every page route */}
            <Route path="/" element={<Home />} />
            <Route path="/oli-family" element={<OliFamily />} />
            <Route path="/fallen-heroes" element={<FallenHerosPage />} />
            <Route path="/blogs" element={<BlogPage />} />
            <Route path="/blogs/:slug" element={<CurrStory />} />
            <Route path="/donate" element={<DonatePage />} />
            <Route path="/donation-success" element={<DonationSuccess />} />
            <Route path="/history" element={<History />} />
            <Route path="/patriotic-signup" element={<PatrioticGardens />} />
            <Route path="/gold-star-families" element={<GoldStarFam />} />
            <Route path="/find-food-bank" element={<FindFoodBank />} />
            <Route path="/annual-gala" element={<AnnualGala />} />
            <Route path="/annual-gala/honorees" element={<GalaHonorees />} />
            <Route path="/patriotic-gardens-event" element={<PatrioticGardensEvent />} />
            <Route path="/programs" element={<Programs />} />
            <Route path="/faq" element={<Faq />} />
            {/* Archived: keep old bookmarks away from the unshipped signup page. */}
            <Route path="/garden-bed-signup" element={<Navigate to="/programs" replace />} />
            <Route path="/meet-greet-grow" element={<MeetGreetGrow />} />
            {/* /team is an alias — OliFamily is the Officers & Board page */}
            <Route path="/team" element={<OliFamily />} />
          </Route>

          {/* Admin routes — the whole CMS stays out of the public bundle */}
          <Route
            path="/admin/login"
            element={
              <Suspense fallback={<RouteFallback />}>
                <AdminLogin />
              </Suspense>
            }
          />
          <Route element={<AdminLayout />}>
            <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
            <Route path="/admin/blog" element={<ProtectedRoute><AdminBlog /></ProtectedRoute>} />
            <Route path="/admin/archive" element={<ProtectedRoute><AdminArchive /></ProtectedRoute>} />
            <Route path="/admin/edit-blog/:slug" element={<ProtectedRoute><AdminEditBlog /></ProtectedRoute>} />
            <Route path="/admin/meet-greet-grow" element={<ProtectedRoute><AdminEventRegistrations /></ProtectedRoute>} />
          </Route>
        </Routes>
      </div>
    </Router>
  );
}

export default App;
